/**
 * 首頁
 * 顯示司機基本資料和開始任務按鈕
 */

import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLiff } from '@/contexts/LiffContext'
import { getDriver, unbindLine } from '@/services/driverService'
import { clearAuthData } from '@/services/apiClient'
import type { Driver } from '@/types'
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  CardContent,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import AssignmentIcon from '@mui/icons-material/Assignment'
import PersonIcon from '@mui/icons-material/Person'
import BusinessIcon from '@mui/icons-material/Business'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import HistoryIcon from '@mui/icons-material/History'
import EditNoteIcon from '@mui/icons-material/EditNote'

const HomePage: React.FC = () => {
  const navigate = useNavigate()
  const { lineUser, idToken, isLiffReady } = useLiff()

  // ========== State ==========
  const [driver, setDriver] = useState<Driver | null>(null)
  const [loading, setLoading] = useState(true)

  // 解除綁定對話框相關
  const [unbindDialogOpen, setUnbindDialogOpen] = useState(false)
  const [unbindCountdown, setUnbindCountdown] = useState(5)
  const [unbinding, setUnbinding] = useState(false)

  // ========== 載入司機資料 ==========
  useEffect(() => {
    const loadDriverData = async () => {
      // 等待 LIFF 初始化完成且有 LINE 使用者資料和 ID Token
      if (!isLiffReady || !lineUser || !idToken) {
        console.log('[HomePage] 等待 LIFF 初始化...', { isLiffReady, hasLineUser: !!lineUser, hasIdToken: !!idToken })
        return
      }

      try {
        const driverData = await getDriver(lineUser.userId)

        if (!driverData) {
          // 司機未綁定，導向綁定頁面
          navigate('/binding', { replace: true })
          return
        }

        setDriver(driverData)
      } catch (error) {
        console.error('載入司機資料失敗:', error)
      } finally {
        setLoading(false)
      }
    }

    loadDriverData()
  }, [lineUser, navigate, idToken, isLiffReady])

  // ========== 解除綁定倒數計時 ==========
  useEffect(() => {
    let timer: NodeJS.Timeout
    if (unbindDialogOpen && unbindCountdown > 0) {
      timer = setTimeout(() => {
        setUnbindCountdown(unbindCountdown - 1)
      }, 1000)
    }
    return () => clearTimeout(timer)
  }, [unbindDialogOpen, unbindCountdown])

  // ========== Event Handlers ==========
  const handleStartTask = () => {
    console.log('[HomePage] handleStartTask 被點擊')
    console.log('[HomePage] driver:', driver)
    console.log('[HomePage] hasUnfinishedDuty:', driver?.hasUnfinishedDuty)

    if (driver?.hasUnfinishedDuty) {
      // 已上工，跳轉到任務列表
      console.log('[HomePage] 導航到 /tasks')
      navigate('/tasks')
    } else {
      // 未上工，跳轉到車輛選擇
      console.log('[HomePage] 導航到 /vehicle-selection')
      navigate('/vehicle-selection')
    }
  }

  // 開啟解除綁定警告對話框
  const handleOpenUnbindDialog = () => {
    setUnbindCountdown(5) // 重置倒數
    setUnbindDialogOpen(true)
  }

  // 關閉解除綁定對話框
  const handleCloseUnbindDialog = () => {
    setUnbindDialogOpen(false)
    setUnbindCountdown(5) // 重置倒數
  }

  // 確認解除綁定
  const handleConfirmUnbind = async () => {
    if (!driver?.driverId) return

    setUnbinding(true)
    try {
      const result = await unbindLine(driver.driverId)
      if (result.success) {
        // 清除本地認證資料
        localStorage.removeItem('driver-binding')
        clearAuthData()
        // 關閉對話框
        setUnbindDialogOpen(false)
        // 導向綁定頁面
        navigate('/binding', { replace: true })
      } else {
        console.error('解除綁定失敗:', result.message)
        alert(`解除綁定失敗：${result.message}`)
      }
    } catch (error) {
      console.error('解除綁定錯誤:', error)
      alert('解除綁定發生錯誤，請稍後再試')
    } finally {
      setUnbinding(false)
    }
  }

  const handleViewHistory = () => {
    // 導向歷史紀錄頁面
    navigate('/daily-report', {
      state: {
        driver: driver,
        fromHome: true,
      },
    })
  }

  // ========== 載入中 ==========
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #FFE8D6 0%, #DDBEA9 100%)',
        }}
      >
        <CircularProgress size={60} sx={{ color: '#6B705C' }} />
        <Typography variant="body1" sx={{ mt: 2, color: '#6B705C' }}>
          載入中...
        </Typography>
      </Box>
    )
  }

  // ========== Render ==========
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #FFE8D6 0%, #DDBEA9 100%)',
        py: 3,
      }}
    >
      <Container maxWidth="sm">
        {/* 司機資料卡片 */}
        <Paper
          elevation={3}
          sx={{
            mb: 3,
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          {/* 頂部背景 */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #6B705C 0%, #A5A58D 100%)',
              p: 3,
              color: 'white',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              {lineUser?.pictureUrl && (
                <img
                  src={lineUser.pictureUrl}
                  alt={lineUser.displayName}
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    border: '3px solid white',
                  }}
                />
              )}
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {driver?.driverName || '載入中...'}
                </Typography>
                <Chip
                  label={driver?.hasUnfinishedDuty ? '工作中' : '休息中'}
                  size="small"
                  sx={{
                    backgroundColor: driver?.hasUnfinishedDuty ? '#ff9800' : '#9e9e9e',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* 司機詳細資訊 */}
          <CardContent sx={{ p: 3 }}>
            {/* 司機編號 */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
              <PersonIcon sx={{ color: '#6B705C', fontSize: 24 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  司機編號
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {driver?.driverId || '-'}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* 所屬公司 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <BusinessIcon sx={{ color: '#6B705C', fontSize: 24 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  所屬公司
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {driver?.companyName || '-'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Paper>

        {/* 主要按鈕：根據狀態顯示「上工」或「查看任務」 */}
        <Button
          fullWidth
          variant="contained"
          size="large"
          startIcon={driver?.hasUnfinishedDuty ? <AssignmentIcon /> : <DirectionsCarIcon />}
          onClick={handleStartTask}
          sx={{
            backgroundColor: driver?.hasUnfinishedDuty ? '#ff9800' : '#6B705C',
            color: 'white',
            py: 2,
            fontSize: '1.2rem',
            fontWeight: 700,
            borderRadius: 3,
            mb: 2,
            boxShadow: driver?.hasUnfinishedDuty
              ? '0 4px 12px rgba(255, 152, 0, 0.3)'
              : '0 4px 12px rgba(107, 112, 92, 0.3)',
            '&:hover': {
              backgroundColor: driver?.hasUnfinishedDuty ? '#f57c00' : '#5A5F4A',
              boxShadow: driver?.hasUnfinishedDuty
                ? '0 6px 16px rgba(255, 152, 0, 0.4)'
                : '0 6px 16px rgba(107, 112, 92, 0.4)',
            },
          }}
        >
          {driver?.hasUnfinishedDuty ? '查看任務' : '上工'}
        </Button>

        {/* 任務補登按鈕 */}
        <Button
          fullWidth
          variant="outlined"
          size="large"
          startIcon={<EditNoteIcon />}
          onClick={() => navigate('/mission-amendment')}
          sx={{
            borderColor: '#A5A58D',
            color: '#A5A58D',
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            borderRadius: 3,
            mb: 2,
            '&:hover': {
              borderColor: '#8F8F75',
              backgroundColor: 'rgba(165, 165, 141, 0.08)',
            },
          }}
        >
          任務補登
        </Button>

        {/* 歷史紀錄按鈕 */}
        <Button
          fullWidth
          variant="outlined"
          size="large"
          startIcon={<HistoryIcon />}
          onClick={handleViewHistory}
          sx={{
            borderColor: '#6B705C',
            color: '#6B705C',
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            borderRadius: 3,
            mb: 2,
            '&:hover': {
              borderColor: '#5A5F4A',
              backgroundColor: 'rgba(107, 112, 92, 0.08)',
            },
          }}
        >
          歷史紀錄
        </Button>

        {/* 解除綁定按鈕 */}
        <Button
          fullWidth
          variant="outlined"
          onClick={handleOpenUnbindDialog}
          sx={{
            borderColor: '#CB997E',
            color: '#CB997E',
            py: 1.5,
            fontWeight: 600,
            '&:hover': {
              borderColor: '#B7866B',
              backgroundColor: 'rgba(203, 153, 126, 0.04)',
            },
          }}
        >
          解除與Line綁定
        </Button>

        {/* 解除綁定警告對話框 */}
        <Dialog
          open={unbindDialogOpen}
          onClose={handleCloseUnbindDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              m: 2,
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: '#f57c00',
              fontWeight: 700,
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 28 }} />
            解除綁定警告
          </DialogTitle>
          <DialogContent>
            <Box sx={{ py: 1 }}>
              <Typography
                variant="body1"
                sx={{
                  mb: 2,
                  p: 2,
                  bgcolor: '#fff3e0',
                  borderRadius: 2,
                  border: '1px solid #ffcc80',
                }}
              >
                解除綁定後，您將<strong>收不到來自 LINE 的任何通知訊息</strong>，包含任務指派、狀態更新等重要通知。
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  mb: 2,
                  p: 2,
                  bgcolor: '#ffebee',
                  borderRadius: 2,
                  border: '1px solid #ef9a9a',
                }}
              >
                如需重新綁定，舊有的驗證碼<strong>「可能」已失效</strong>。若驗證碼失效，請聯繫系統管理人員重新申請。
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', mt: 3 }}
              >
                請確認您已了解上述風險
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, gap: 1, flexDirection: 'column' }}>
            <Button
              fullWidth
              variant="contained"
              color="error"
              onClick={handleConfirmUnbind}
              disabled={unbindCountdown > 0 || unbinding}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              {unbinding
                ? '處理中...'
                : unbindCountdown > 0
                ? `確認解除綁定 (${unbindCountdown})`
                : '確認解除綁定'}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleCloseUnbindDialog}
              disabled={unbinding}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              取消
            </Button>
          </DialogActions>
        </Dialog>

        {/* 版本資訊 */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            好運網 v1.0.0
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 1,
              fontSize: '0.7rem',
              color: '#999',
            }}
          >
            LIFF: {import.meta.env.VITE_LIFF_ID || '-'}
          </Typography>
        </Box>
      </Container>
    </Box>
  )
}

export default HomePage
